import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { quizService } from '@/services/quizService'
import type { CreateQuizRequest, Question, GradeQuizAttemptRequest } from '@/types/quiz'

// Get all quizzes with lesson/module/course info
export function useAllQuizzes() {
  return useQuery({
    queryKey: ['quizzes', 'all'],
    queryFn: () => quizService.listAllQuizzes(),
  })
}

// Get quiz by lesson ID
export function useQuizByLesson(lessonId: string) {
  return useQuery({
    queryKey: ['quizzes', 'lesson', lessonId],
    queryFn: async () => {
      const quizzes = await quizService.getQuizzesByLesson(lessonId)
      // Return first quiz or null (typically a lesson has 0 or 1 quiz)
      return quizzes.length > 0 ? quizzes[0] : null
    },
    enabled: !!lessonId,
  })
}

// Get quiz by ID
export function useQuiz(quizId: string) {
  return useQuery({
    queryKey: ['quizzes', quizId],
    queryFn: () => quizService.getQuiz(quizId),
    enabled: !!quizId,
  })
}

// Get questions for a quiz
export function useQuestions(quizId: string) {
  return useQuery({
    queryKey: ['questions', quizId],
    queryFn: () => quizService.getQuestions(quizId),
    enabled: !!quizId,
  })
}

// Create a new quiz
export function useCreateQuiz() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateQuizRequest) => quizService.createQuiz(data),
    onSuccess: (quiz) => {
      queryClient.invalidateQueries({ queryKey: ['quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['quizzes', 'lesson', quiz.lessonId] })
    },
  })
}

// Update a quiz
export function useUpdateQuiz() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateQuizRequest> }) =>
      quizService.updateQuiz(id, data),
    onSuccess: (quiz) => {
      queryClient.invalidateQueries({ queryKey: ['quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['quizzes', quiz.id] })
      queryClient.invalidateQueries({ queryKey: ['quizzes', 'lesson', quiz.lessonId] })
    },
  })
}

// Delete a quiz
export function useDeleteQuiz() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => quizService.deleteQuiz(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quizzes'] })
    },
  })
}

// Publish a quiz
export function usePublishQuiz() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => quizService.publishQuiz(id),
    onSuccess: (quiz) => {
      queryClient.invalidateQueries({ queryKey: ['quizzes'] })
      if (quiz) {
        queryClient.invalidateQueries({ queryKey: ['quizzes', quiz.id] })
        queryClient.invalidateQueries({ queryKey: ['quizzes', 'lesson', quiz.lessonId] })
      }
    },
  })
}

// Add a question to a quiz
export function useAddQuestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      quizId,
      question,
    }: {
      quizId: string
      question: Omit<Question, 'id' | 'quizId'>
    }) => quizService.addQuestion(quizId, question),
    onSuccess: (question) => {
      queryClient.invalidateQueries({ queryKey: ['questions', question.quizId] })
      queryClient.invalidateQueries({ queryKey: ['quizzes', question.quizId] })
    },
  })
}

// Update a question (we need to add this to the service)
export function useUpdateQuestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      quizId,
      questionId,
      question,
    }: {
      quizId: string
      questionId: string
      question: Partial<Omit<Question, 'id' | 'quizId'>>
    }) => {
      const { apiClient } = await import('@/services/api')
      const response = await apiClient.put<Question>(`/quizzes/${quizId}/questions/${questionId}`, question)
      return response.data as Question
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['questions', variables.quizId] })
      queryClient.invalidateQueries({ queryKey: ['quizzes', variables.quizId] })
    },
  })
}

// Delete a question
export function useDeleteQuestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ quizId, questionId }: { quizId: string; questionId: string }) => {
      const { apiClient } = await import('@/services/api')
      await apiClient.delete<void>(`/quizzes/${quizId}/questions/${questionId}`)
      return { quizId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['questions', result.quizId] })
      queryClient.invalidateQueries({ queryKey: ['quizzes', result.quizId] })
    },
  })
}

// Reorder questions
export function useReorderQuestions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ quizId, questionIds }: { quizId: string; questionIds: string[] }) => {
      const { apiClient } = await import('@/services/api')
      await apiClient.put<void>(`/quizzes/${quizId}/questions/reorder`, { questionIds })
      return { quizId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['questions', result.quizId] })
    },
  })
}

// Start quiz attempt (Student)
export function useStartQuizAttempt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ quizId, enrollmentId }: { quizId: string; enrollmentId: string }) =>
      quizService.startQuizAttempt(quizId, enrollmentId),
    onSuccess: (attempt) => {
      queryClient.invalidateQueries({ queryKey: ['quiz-attempts'] })
      queryClient.invalidateQueries({ queryKey: ['quiz-attempts', attempt.enrollmentId] })
    },
  })
}

// Submit quiz attempt (Student)
export function useSubmitQuizAttempt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ attemptId, answers }: { attemptId: string; answers: Record<string, number | string> }) =>
      quizService.submitQuizAttempt(attemptId, answers),
    onSuccess: (result) => {
      // Invalidate all related caches
      queryClient.invalidateQueries({ queryKey: ['quiz-attempts'] })
      queryClient.invalidateQueries({ queryKey: ['quiz-attempts', result.enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['progress'] })
      queryClient.invalidateQueries({ queryKey: ['enrollments'], exact: false })
    },
  })
}

// Get quiz attempts summary (Admin only)
export function useQuizAttemptsSummary(quizId: string) {
  return useQuery({
    queryKey: ['quiz-attempts-summary', quizId],
    queryFn: () => quizService.getQuizAttemptsSummary(quizId),
    enabled: !!quizId,
  })
}

// Reset quiz attempts (Admin only)
export function useResetQuizAttempts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ quizId, enrollmentId }: { quizId: string; enrollmentId: string }) =>
      quizService.resetQuizAttempts(quizId, enrollmentId),
    onSuccess: (_, variables) => {
      // Invalidate all quiz attempts cache
      queryClient.invalidateQueries({ queryKey: ['quiz-attempts'] })
      queryClient.invalidateQueries({ queryKey: ['quiz-attempts-summary', variables.quizId] })
      queryClient.invalidateQueries({ queryKey: ['progress'] })
    },
  })
}

// Get quiz attempts with student info (Instructor/Admin)
export function useQuizAttempts(quizId: string, statusFilter?: string) {
  return useQuery({
    queryKey: ['quiz-attempts-review', quizId, statusFilter],
    queryFn: () => quizService.getQuizAttempts(quizId, statusFilter),
    enabled: !!quizId,
  })
}

// Get quiz attempt detail (Instructor/Admin)
export function useQuizAttemptDetail(attemptId: string) {
  return useQuery({
    queryKey: ['quiz-attempt-detail', attemptId],
    queryFn: () => quizService.getQuizAttemptDetail(attemptId),
    enabled: !!attemptId,
  })
}

// Grade quiz attempt (Instructor/Admin)
export function useGradeQuizAttempt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ attemptId, data }: { attemptId: string; data: GradeQuizAttemptRequest }) =>
      quizService.gradeQuizAttempt(attemptId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quiz-attempts-review'] })
      queryClient.invalidateQueries({ queryKey: ['quiz-attempt-detail', variables.attemptId] })
      queryClient.invalidateQueries({ queryKey: ['quiz-attempts-summary'] })
    },
  })
}
